import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Container, Row, Col, Form, Button, Card, Badge } from "react-bootstrap";
import axios from "axios";
import CreatableSelect from 'react-select/creatable';
import { useAuth } from './auth/AuthContext';
import { compareEventsByDisplayOrder, sortEventsByDisplayOrder } from '../utils/eventSort';

export function EventsAdmin() {
    const [events, setEvents] = useState([]);
    const [sponsors, setSponsors] = useState([]);
    const [auditEntries, setAuditEntries] = useState([]);
    const [auditLoading, setAuditLoading] = useState(false);
    const [crewFilter, setCrewFilter] = useState("all");
    const [sortMode, setSortMode] = useState("draw");
    const [sponsorEditEventId, setSponsorEditEventId] = useState(null);
    const [sponsorSavingEventId, setSponsorSavingEventId] = useState(null);
    const [offsetSavingEventId, setOffsetSavingEventId] = useState(null);
    const [titleEditEventId, setTitleEditEventId] = useState(null);
    const [titleDraft, setTitleDraft] = useState('');
    const [titleSavingEventId, setTitleSavingEventId] = useState(null);

    const { getToken, isAdmin } = useAuth();

    const fetchData = useCallback(async () => {
        try {
            const token = getToken();
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };
            setAuditLoading(true);
            
            const [eventRes, sponsorRes, auditRes] = await Promise.all([
                axios.get("/api/event", config),
                axios.get("/api/sponsor", config),
                axios.get('/api/event/audit?limit=100', config).catch(() => ({ data: [] }))
            ]);
            setEvents(eventRes.data);
            setSponsors(sponsorRes.data);
            setAuditEntries(Array.isArray(auditRes?.data) ? auditRes.data : []);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setAuditLoading(false);
        }
    }, [getToken]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getSponsorName = (id) =>
        sponsors.find(s => s.sponsorId === id)?.sponsorName || '-';

    const sponsorOptions = useMemo(
        () => sponsors
            .map((sponsor) => ({ value: Number(sponsor.sponsorId), label: String(sponsor.sponsorName || '').trim() }))
            .filter((option) => Number.isInteger(option.value) && option.value > 0 && option.label)
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
        [sponsors]
    );

    const showableEvents = useMemo(
        () => sortEventsByDisplayOrder(events.filter(event => event.crewsEntered > 1 || event.sponsorId)),
        [events]
    );

    const filteredEvents = useMemo(() => {
        if (crewFilter === "multi") {
            return showableEvents.filter(event => event.crewsEntered > 1);
        }

        if (crewFilter === "single") {
            return showableEvents.filter(event => event.crewsEntered === 1);
        }

        if (crewFilter === "sponsorNoCrews") {
            return showableEvents.filter(event => event.sponsorId && event.crewsEntered === 0);
        }

        return showableEvents;
    }, [crewFilter, showableEvents]);

    const groupedEvents = useMemo(() => {
        const allGroups = new Map();

        events.forEach((event) => {
            const ownId = Number(event.id);
            const parentId = Number(event.parentEventId || 0);
            const overallEventId = parentId > 0 ? parentId : ownId;

            if (!allGroups.has(overallEventId)) {
                allGroups.set(overallEventId, {
                    overallEventId,
                    allMembers: [],
                    visibleMembers: []
                });
            }
            allGroups.get(overallEventId).allMembers.push(event);
        });

        const visibleEventIds = new Set(filteredEvents.map((event) => Number(event.id)));
        allGroups.forEach((group) => {
            group.visibleMembers = group.allMembers
                .filter((member) => visibleEventIds.has(Number(member.id)))
                .sort(compareEventsByDisplayOrder);

            group.allMembers = group.allMembers.sort(compareEventsByDisplayOrder);
            group.totalCrews = group.allMembers.reduce((sum, member) => sum + Number(member.crewsEntered || 0), 0);

            const parentMember = group.allMembers.find((member) => Number(member.id) === Number(group.overallEventId));
            group.primaryEvent = parentMember || group.allMembers[0] || null;
            group.isBandedGroup = group.allMembers.length > 1
                || group.allMembers.some((member) => Number(member.parentEventId || 0) > 0)
                || group.allMembers.some((member) => Boolean(member.hasBands));
        });

        const visibleGroups = [...allGroups.values()]
            .filter((group) => group.visibleMembers.length > 0)
            .sort((a, b) => {
                if (sortMode === 'size') {
                    const sizeDiff = Number(b.totalCrews || 0) - Number(a.totalCrews || 0);
                    if (sizeDiff !== 0) return sizeDiff;
                }

                return compareEventsByDisplayOrder(a.primaryEvent || {}, b.primaryEvent || {});
            });

        return visibleGroups;
    }, [events, filteredEvents, sortMode]);

    const summary = useMemo(() => ({
        totalVisible: showableEvents.length,
        singleCrew: showableEvents.filter(event => event.crewsEntered === 1).length,
        multiCrew: showableEvents.filter(event => event.crewsEntered > 1).length,
        sponsorNoCrews: showableEvents.filter(event => event.sponsorId && event.crewsEntered === 0).length
    }), [showableEvents]);

    function formatAuditTimestamp(value) {
        if (!value) return '--';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return String(value);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    function formatAuditChange(change) {
        const field = String(change?.field || '').trim();
        const before = change?.before;
        const after = change?.after;

        if (field === 'sponsorId') {
            const beforeText = before == null ? 'none' : `#${before}`;
            const afterText = after == null ? 'none' : `#${after}`;
            return `Sponsor ${beforeText} -> ${afterText}`;
        }

        if (field === 'timetableOffset') {
            return `Offset ${Number(before || 0)} -> ${Number(after || 0)}`;
        }

        if (field === 'eventTitle') {
            return `Title "${String(before || '')}" -> "${String(after || '')}"`;
        }

        return `${field}: ${String(before ?? '')} -> ${String(after ?? '')}`;
    }

    function formatAuditDetails(entry) {
        const details = entry?.details || {};
        if (entry?.action === 'event_admin_create_sponsor') {
            return `Created sponsor "${String(details?.sponsorName || '')}" (id ${details?.sponsorId || '-'})`;
        }

        if (entry?.action === 'event_admin_update') {
            const eventTitle = String(details?.eventTitle || '').trim();
            const eventLabel = eventTitle || `Event ${details?.eventId || '-'}`;
            const changes = Array.isArray(details?.changes) ? details.changes : [];
            if (!changes.length) return `${eventLabel}: updated`;
            return `${eventLabel}: ${changes.map(formatAuditChange).join('; ')}`;
        }

        return String(entry?.action || 'updated');
    }

    function shouldHighlight(event) {
        return (
            (!event.sponsorId && event.crewsEntered > 0) ||
            (event.sponsorId && event.crewsEntered === 0)
        );
    }

    async function createSponsor(sponsorName) {
        const sponsorLabel = String(sponsorName || '').trim();
        if (!sponsorLabel) return null;
        try {
            const token = getToken();
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            const res = await axios.post('/api/sponsor', { sponsorName: sponsorLabel }, config);
            return res.data || null;
        } catch (error) {
            console.error('Error creating sponsor:', error);
            return null;
        }
    }

    async function updateEventSponsor(event, sponsorId) {
        const parsedSponsorId = Number(sponsorId);
        const safeSponsorId = Number.isFinite(parsedSponsorId) && parsedSponsorId > 0 ? parsedSponsorId : null;

        try {
            setSponsorSavingEventId(event.id);
            const token = getToken();
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            const payload = {
                eventId: event.id,
                sponsorId: safeSponsorId
            };

            if (isAdmin) {
                payload.eventTitle = event.title;
                payload.timetableOffset = event.timetableOffset;
            }

            await axios.put(`/api/event/${event.id}`, payload, config);
            await fetchData();
            setSponsorEditEventId(null);
        } catch (error) {
            console.error('Error updating sponsor:', error);
        } finally {
            setSponsorSavingEventId(null);
        }
    }

    async function handleSponsorSelection(event, option) {
        if (!option) {
            await updateEventSponsor(event, null);
            return;
        }

        if (option.__isNew__) {
            const created = await createSponsor(option.label);
            if (!created?.sponsorId) return;
            setSponsors((prev) => [...prev, created].sort((a, b) => a.sponsorName.localeCompare(b.sponsorName)));
            await updateEventSponsor(event, created.sponsorId);
            return;
        }

        await updateEventSponsor(event, option.value);
    }

    async function adjustTimetableOffset(event, delta) {
        if (!isAdmin) return;

        const currentOffset = Number(event.timetableOffset || 0);
        const nextOffset = currentOffset + Number(delta || 0);

        try {
            setOffsetSavingEventId(event.id);
            const token = getToken();
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            await axios.put(`/api/event/${event.id}`, {
                eventId: event.id,
                eventTitle: event.title,
                sponsorId: event.sponsorId ?? null,
                timetableOffset: nextOffset
            }, config);

            await fetchData();
        } catch (error) {
            console.error('Error updating timetable offset:', error);
        } finally {
            setOffsetSavingEventId(null);
        }
    }

    function beginEventTitleEdit(event) {
        if (!isAdmin) return;
        setTitleEditEventId(event.id);
        setTitleDraft(String(event.title || ''));
    }

    function cancelEventTitleEdit() {
        setTitleEditEventId(null);
        setTitleDraft('');
    }

    async function saveEventTitle(event) {
        if (!isAdmin) return;
        if (titleSavingEventId === event.id) return;

        const nextTitle = String(titleDraft || '').trim();
        const currentTitle = String(event.title || '').trim();
        if (!nextTitle || nextTitle === currentTitle) {
            cancelEventTitleEdit();
            return;
        }

        try {
            setTitleSavingEventId(event.id);
            const token = getToken();
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            await axios.put(`/api/event/${event.id}`, {
                eventId: event.id,
                eventTitle: nextTitle,
                sponsorId: event.sponsorId ?? null,
                timetableOffset: Number(event.timetableOffset || 0)
            }, config);

            cancelEventTitleEdit();
            await fetchData();
        } catch (error) {
            console.error('Error updating event title:', error);
        } finally {
            setTitleSavingEventId(null);
        }
    }

    return (
        <Container className="mt-4">
            <Card className="shadow-sm border-0">
                <Card.Body>
                    <Row className="g-2 align-items-end mb-3">
                        <Col xs={12} md={6}>
                            <h4 className="mb-1">Event Sponsor and Timetable Admin</h4>
                            <div className="text-muted" style={{ fontSize: 13 }}>
                                Crew counts include accepted, submitted and scratched crews.
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Group className="mb-2">
                                <Form.Label className="mb-1" style={{ fontSize: 13 }}>Sort events</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={sortMode}
                                    onChange={(e) => setSortMode(e.target.value)}
                                >
                                    <option value="draw">Default event sorting (draw order)</option>
                                    <option value="size">Overall event size (largest first)</option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label className="mb-1" style={{ fontSize: 13 }}>Filter events</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={crewFilter}
                                    onChange={(e) => setCrewFilter(e.target.value)}
                                >
                                    <option value="all">All events with 2+ crews or sponsor ({summary.totalVisible})</option>
                                    <option value="multi">Only events with 2+ crews ({summary.multiCrew})</option>
                                    <option value="single">Only events with 1 crew ({summary.singleCrew})</option>
                                    <option value="sponsorNoCrews">Sponsored events with no crews ({summary.sponsorNoCrews})</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                            <thead>
                                <tr>
                                    <th style={{ minWidth: 220 }}>Event</th>
                                    <th style={{ width: 90 }}>Crews</th>
                                    <th style={{ minWidth: 220 }}>Sponsor</th>
                                    {isAdmin && <th style={{ width: 260 }}>Timetable Offset</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {groupedEvents.map((group) => (
                                    <React.Fragment key={`group-${group.overallEventId}`}>
                                        {group.isBandedGroup && (
                                            <tr className="table-light">
                                                <td style={{ fontWeight: 600 }}>
                                                    {group.primaryEvent?.title || `Event ${group.overallEventId}`}
                                                </td>
                                                <td>
                                                    <Badge bg="secondary">Overall crews: {group.totalCrews}</Badge>
                                                </td>
                                                <td colSpan={isAdmin ? 2 : 1}></td>
                                            </tr>
                                        )}
                                        {group.visibleMembers.map((event) => {
                                    return (
                                                <tr key={event.id}>
                                            <td>
                                                {titleEditEventId === event.id ? (
                                                    <Form.Control
                                                        size="sm"
                                                        autoFocus
                                                        value={titleDraft}
                                                        disabled={titleSavingEventId === event.id}
                                                        onChange={(evt) => setTitleDraft(evt.target.value)}
                                                        onBlur={() => {
                                                            if (titleSavingEventId !== event.id) {
                                                                saveEventTitle(event);
                                                            }
                                                        }}
                                                        onKeyDown={(evt) => {
                                                            if (evt.key === 'Enter') {
                                                                evt.preventDefault();
                                                                saveEventTitle(event);
                                                            }
                                                            if (evt.key === 'Escape') {
                                                                evt.preventDefault();
                                                                cancelEventTitleEdit();
                                                            }
                                                        }}
                                                    />
                                                ) : (
                                                    <div
                                                        className={shouldHighlight(event) ? "highlight-title" : ""}
                                                        style={{ lineHeight: 1.15, cursor: isAdmin ? 'text' : 'default' }}
                                                        role={isAdmin ? 'button' : undefined}
                                                        tabIndex={isAdmin ? 0 : undefined}
                                                        title={isAdmin ? 'Double-click to rename event' : undefined}
                                                        onDoubleClick={() => beginEventTitleEdit(event)}
                                                        onKeyDown={(evt) => {
                                                            if (!isAdmin) return;
                                                            if (evt.key === 'Enter' || evt.key === ' ') {
                                                                evt.preventDefault();
                                                                beginEventTitleEdit(event);
                                                            }
                                                        }}
                                                    >
                                                        {event.title}
                                                    </div>
                                                )}
                                                <div className="text-muted" style={{ fontSize: 12 }}>
                                                    {event.id} | {event.oaraId}
                                                </div>
                                            </td>
                                            <td className={group.isBandedGroup ? "text-end" : ""}>{event.crewsEntered}</td>
                                            <td>
                                                {sponsorEditEventId === event.id ? (
                                                    <CreatableSelect
                                                        autoFocus
                                                        isClearable
                                                        menuPlacement="auto"
                                                        options={sponsorOptions}
                                                        value={sponsorOptions.find((option) => option.value === Number(event.sponsorId)) || null}
                                                        isDisabled={sponsorSavingEventId === event.id}
                                                        placeholder="Search or type sponsor"
                                                        formatCreateLabel={(value) => `Create sponsor: ${value}`}
                                                        onChange={(option) => handleSponsorSelection(event, option)}
                                                        onBlur={() => {
                                                            if (sponsorSavingEventId !== event.id) setSponsorEditEventId(null);
                                                        }}
                                                    />
                                                ) : (
                                                    <span
                                                        role="button"
                                                        tabIndex={0}
                                                        title="Double-click to edit sponsor"
                                                        onDoubleClick={() => setSponsorEditEventId(event.id)}
                                                        onKeyDown={(evt) => {
                                                            if (evt.key === 'Enter' || evt.key === ' ') {
                                                                evt.preventDefault();
                                                                setSponsorEditEventId(event.id);
                                                            }
                                                        }}
                                                    >
                                                        {getSponsorName(event.sponsorId)}
                                                    </span>
                                                )}
                                            </td>
                                            {isAdmin && (
                                                <td>
                                                    <div className="d-flex align-items-center" style={{ gap: 6 }}>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-secondary"
                                                            disabled={offsetSavingEventId === event.id}
                                                            onClick={() => adjustTimetableOffset(event, -1)}
                                                        >
                                                            Reduce Gap
                                                        </Button>
                                                        <span style={{ minWidth: 28, textAlign: 'center', fontWeight: 600 }}>
                                                            {Number(event.timetableOffset || 0)}
                                                        </span>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-secondary"
                                                            disabled={offsetSavingEventId === event.id}
                                                            onClick={() => adjustTimetableOffset(event, 1)}
                                                        >
                                                            Increase Gap
                                                        </Button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card.Body>
            </Card>

            <Card className="shadow-sm border-0 mt-3">
                <Card.Body>
                    <Row className="g-2 align-items-end mb-3">
                        <Col xs={12} md={8}>
                            <h5 className="mb-1">Event Admin Audit Trail</h5>
                            <div className="text-muted" style={{ fontSize: 13 }}>
                                Latest sponsor and timetable offset changes for this meeting.
                            </div>
                        </Col>
                        <Col xs={12} md={4} className="text-md-end">
                            <Button size="sm" variant="outline-secondary" onClick={fetchData}>
                                Refresh Audit
                            </Button>
                        </Col>
                    </Row>

                    <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                            <thead>
                                <tr>
                                    <th style={{ width: 170 }}>When</th>
                                    <th style={{ width: 220 }}>Who</th>
                                    <th>What changed</th>
                                </tr>
                            </thead>
                            <tbody>
                                {auditLoading ? (
                                    <tr>
                                        <td colSpan={3} className="text-muted">Loading audit entries...</td>
                                    </tr>
                                ) : auditEntries.length === 0 ? (
                                    <tr>
                                        <td colSpan={3} className="text-muted">No audit entries yet.</td>
                                    </tr>
                                ) : (
                                    auditEntries.map((entry) => (
                                        <tr key={`audit-${entry.id}`}>
                                            <td>{formatAuditTimestamp(entry.createdAt)}</td>
                                            <td>
                                                <div>{entry.actorName || '-'}</div>
                                                {entry.actorEmail ? (
                                                    <div className="text-muted" style={{ fontSize: 12 }}>{entry.actorEmail}</div>
                                                ) : null}
                                            </td>
                                            <td>{formatAuditDetails(entry)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card.Body>
            </Card>
        </Container>
    );
}